/**
 * Batch family creation for the Add Families wizard.
 *
 * Each row in the roster becomes:
 *   1. A `Contact` (parent) under `organizations/{orgId}/contacts/{contactId}`
 *   2. A `Student` (child) under `organizations/{orgId}/students/{studentId}`
 *      with `family[contactId] = contact` and `familyMembers = [contactId]`
 *
 * Two tuition-shaped fields are written per student:
 *
 *   * `enrollment.billingPlan` — only set when the wizard is actively
 *     putting the family on Wonderschool billing (bulk plan from Step 3
 *     when `addToBilling` is ON, or per-row when it's OFF and an amount
 *     is typed). Drives the existing invoice-plan / Stripe flow.
 *
 *   * `currentTuition` (also mirrored on `enrollment.currentTuition`) —
 *     captured on EVERY family that has a rate on file, even when they
 *     aren't being put on Wonderschool billing. This is the value the
 *     Annual Pricing Increases tool ("Update your pricing") reads to
 *     propose year-over-year rate changes. Same shape, different
 *     intent: billingPlan = "what we charge them", currentTuition =
 *     "what they pay" (often the same number, but kept separate so
 *     APR has a starting rate even for families we're not invoicing).
 *
 * Mirrors the field shape `StudentAddPage` writes, so families created
 * here are indistinguishable from ones added through the single-student
 * form.
 */
import { ContactStatus, ContactType } from '@wonderschool/common-base-types';
import { firestore } from '../../api/firebase/firebase';
import { fetchContactsByEmail, saveContact } from '../../contacts/contactsAPI';
import { INVITATION_TYPES } from '../../contacts/enums';
import { EnrollmentSourceEnum, EnrollmentStatusEnum } from '../../enrollments';
import { isTodayOrBefore } from '../../helpers/dates';
import { formatFullName, phoneNumberFormat } from '../../helpers/utils';
import { logError, logInfo } from '../../rollbar';
import { saveStudent } from '../../students/studentsAPI';
import type { RowMatch } from './matchExisting';
import type { BulkBilling, RosterRow } from './types';

export interface ConfirmResult {
  added: number;
  contactsCreated: number;
  studentsCreated: number;
  // Parent contacts written with `status: inviting`, which is what actually
  // triggers the invitation email server-side (see the note on `saveRow`).
  invitesQueued: number;
  errors: Array<{ row: RosterRow; error: string }>;
  // Rows whose child was already in the system (matched at confirm time) and
  // was therefore left exactly as-is — never re-created or overwritten. Rows
  // matched as billable (existing child, tuition typed, not yet on a plan)
  // are ALSO not re-created, but do appear in `billableStudentsByRowId`.
  existingMatched: number;
  // The student docs the caller may put on billing, keyed by roster row id:
  // students this run created, plus existing students matched as "add to
  // billing". The recurring-plan payload embeds the real student doc as its
  // payer, and only rows that actually exist in Firestore may be billed.
  // Shape matches what `confirmAddToBilling` expects in `studentsById`.
  billableStudentsByRowId: Map<string, Record<string, any>>;
}

interface ConfirmOptions {
  organizationId: string;
  locationId?: string;
  defaultRoomId?: string;
  rows: RosterRow[];
  addToBilling: boolean;
  sendEmailsLater: boolean;
  bulkBilling?: BulkBilling;
  // Per-row existing-family classification (same order/length as `rows`),
  // computed by `matchRosterAgainstExisting`. When present, a row whose child
  // already exists is handled NON-destructively: no student/contact re-create;
  // an "add billing" match only exposes the existing student for the caller's
  // plan-creation step. When absent, every row is treated as new (the
  // original behavior).
  matches?: RowMatch[];
}

/**
 * Save each roster row to Firestore + queue invitations. Returns counts
 * and per-row errors so the Done step can surface them. Continues on
 * per-row error (the prototype's behavior — one bad row shouldn't kill
 * the whole batch).
 */
export async function confirmFamilies(opts: ConfirmOptions): Promise<ConfirmResult> {
  const result: ConfirmResult = {
    added: 0,
    contactsCreated: 0,
    studentsCreated: 0,
    invitesQueued: 0,
    errors: [],
    existingMatched: 0,
    billableStudentsByRowId: new Map(),
  };

  if (!opts.organizationId) {
    throw new Error('Organization Id is required');
  }

  // Maps a lowercased parent email → the Contact doc already used for it,
  // whether looked up in Firestore or just created a few rows ago in this
  // same batch. Two siblings sharing a parent email must land on ONE Contact
  // document (see `findExistingContact`); without this, each row would
  // blindly create its own duplicate parent contact.
  const contactCache = new Map<string, Record<string, any>>();

  for (let i = 0; i < opts.rows.length; i++) {
    const row = opts.rows[i];
    const match = opts.matches?.[i];

    // Existing child (matched at confirm time): never re-create or overwrite.
    // A billable match (tuition typed, not on a plan yet) only exposes the
    // student that's already there so the caller can add a recurring plan;
    // everything else is left exactly as-is.
    if (match && match.status !== 'new') {
      result.existingMatched += 1;
      if (match.status === 'existing_add_billing' && match.existingStudent) {
        result.billableStudentsByRowId.set(row.id, match.existingStudent);
      }
      continue;
    }

    try {
      const { contactDocRef, studentDocRef, studentData, contactWasExisting } = await saveRow(opts, row, contactCache);
      result.added += 1;
      if (contactDocRef && !contactWasExisting) result.contactsCreated += 1;
      if (studentDocRef) {
        result.studentsCreated += 1;
        result.billableStudentsByRowId.set(row.id, { ...studentData, id: studentDocRef.id });
      }

      // The invite was already sent by writing the contact with
      // `status: inviting` (see `saveRow`); this only counts it. Only NEW
      // contacts are invited: an existing parent is already on file, and
      // siblings sharing a parent produce ONE contact, so ONE invite.
      if (!opts.sendEmailsLater && !contactWasExisting && row.parentEmail.trim()) {
        result.invitesQueued += 1;
      }
    } catch (err: any) {
      logError('AddFamilies confirm row failed', { row, err });
      result.errors.push({ row, error: err?.message || String(err) });
    }
  }

  // Don't log the full student docs — counts and per-row errors are enough.
  logInfo('AddFamilies confirm complete', { ...result, billableStudentsByRowId: result.billableStudentsByRowId.size });
  return result;
}

interface SavedRefs {
  contactDocRef: { id: string };
  studentDocRef: { id: string };
  // The student doc as written, so the caller can build billing payloads
  // without re-fetching (the doc id is on `studentDocRef`).
  studentData: Record<string, any>;
  // True when the parent Contact already existed (same org, same email) and
  // was reused instead of created, so the caller doesn't over-count creates.
  contactWasExisting: boolean;
}

// The always-visible "Child name" field writes only to `childFirstName`, so a
// provider who types "Sam Doe" (no explicit last name in the optional expander)
// would otherwise persist firstName="Sam Doe", lastName="" — an awkward
// displayName on invoices and in the parent app. Split a single name on the
// LAST space into first + last, but only when a last name wasn't explicitly
// entered. Never mangle a name the provider deliberately split.
function splitChildName(row: RosterRow): { firstName: string; lastName: string } {
  const first = row.childFirstName.trim();
  const last = row.childLastName.trim();
  if (last) return { firstName: first, lastName: last };
  const lastSpace = first.lastIndexOf(' ');
  if (lastSpace <= 0) return { firstName: first, lastName: '' };
  return { firstName: first.slice(0, lastSpace).trim(), lastName: first.slice(lastSpace + 1).trim() };
}

/**
 * Look up an existing Contact by email — first in this batch's in-memory
 * cache (siblings entered a few rows apart in the same submit), then in
 * Firestore (a parent already on file from a prior enrollment). Returns
 * undefined when no match is found, meaning `saveContact` should create a
 * new document. Lookup failures fall back to creating a new contact — a
 * missed match means at worst a duplicate the director can merge, never a
 * blocked save.
 */
async function findExistingContact(
  organizationId: string,
  email: string,
  contactCache: Map<string, Record<string, any>>
): Promise<Record<string, any> | undefined> {
  const key = email.trim().toLowerCase();
  if (!key) return undefined;
  if (contactCache.has(key)) return contactCache.get(key);

  try {
    const existing = (await fetchContactsByEmail(organizationId, key))[0];
    if (existing) contactCache.set(key, existing);
    return existing;
  } catch (err) {
    logError('AddFamilies: contact-by-email lookup failed, creating new contact', { organizationId, err });
    return undefined;
  }
}

async function saveRow(
  opts: ConfirmOptions,
  row: RosterRow,
  contactCache: Map<string, Record<string, any>>
): Promise<SavedRefs> {
  const { organizationId, locationId, defaultRoomId } = opts;
  // Per-student room chosen in Step 2 (a real room id), falling back to the org
  // default. Must be a real room or the room-scoped students list hides them.
  const roomId = row.room || defaultRoomId || '';

  // Roster import is for families already attending, so they're enrolled as of
  // their start date (defaults to the upload date). But a future start date stays
  // PENDING until it arrives, matching the single-student AddStudentPage flow, so
  // a future-term cohort isn't counted as enrolled early.
  const enrolled = !!row.startDate && isTodayOrBefore(row.startDate);

  // Writing the contact with `status: inviting` IS how the invitation is sent:
  // onOrganizationContactCreated creates the invitation doc, whose own trigger
  // sends the email and flips the contact to `invited`. Do NOT also call
  // `inviteContact` — a second write races that trigger and can land as
  // invited -> inviting, which fires `resendInvitation` and puts the invitation
  // into RESENDING. `processSignupData` only accepts a PENDING invitation, so a
  // parent clicking their link mid-race gets a dead invitation. This mirrors
  // StudentAddPage, which likewise writes the contact once and stops.
  const status: ContactStatus = opts.sendEmailsLater ? ContactStatus.new : ContactStatus.inviting;

  const child = splitChildName(row);

  // Reuse an existing parent Contact (same org, same email) instead of
  // creating a duplicate for every sibling — see `findExistingContact`.
  const existingContact = await findExistingContact(organizationId, row.parentEmail, contactCache);

  // Contact + student are written in one Firestore batch so they commit
  // atomically — without this, a student-write failure right after a
  // successful contact-write would leave an orphaned parent Contact with no
  // linked child (and a re-attempt would then hit the just-created contact
  // as a "duplicate" via `findExistingContact`). `saveDocument` pre-issues
  // the doc ref synchronously in batch mode, so `.id` is available below
  // before the batch actually commits.
  const batch = firestore().batch();

  // An EXISTING contact is never rewritten from the roster row — the person
  // on file is authoritative. Overwriting name/phone/status/rooms from an
  // import could demote a verified parent or remove room/location access,
  // and the contact-update cascade would propagate the stale snapshot to
  // every linked student. New children are attached via `studentIds` only
  // (in the batch, below).
  let contactData: any;
  if (existingContact) {
    contactData = existingContact;
  } else {
    contactData = {
      firstName: row.parentFirstName.trim(),
      lastName: row.parentLastName.trim(),
      displayName: formatFullName({ firstName: row.parentFirstName, lastName: row.parentLastName }, true),
      email: row.parentEmail.trim().toLowerCase(),
      phone: phoneNumberFormat(row.parentPhone),
      type: ContactType.FAMILY,
      relationship: 'parent',
      status,
      defaultOrganization: organizationId,
      defaultLocation: locationId ?? '',
      defaultRoom: roomId,
      organizations: [organizationId],
      locations: locationId ? [locationId] : [],
      rooms: roomId ? [roomId] : [],
      allowLoginWithPin: true,
      invitationType: INVITATION_TYPES.parent,
      student: {
        firstName: child.firstName,
        lastName: child.lastName,
        displayName: formatFullName({ firstName: child.firstName, lastName: child.lastName }, true),
        organization: organizationId,
      },
    };
    const newContactRef = await saveContact(organizationId, contactData, { merge: true }, batch);
    contactData.id = newContactRef.id;
  }
  const contactDocRef = { id: contactData.id };

  // Build student doc — same shape StudentAddPage writes
  const family: Record<string, any> = { [contactDocRef.id]: contactData };
  const familyMembers: string[] = [contactDocRef.id];

  const billingPlan = computeBillingPlan(opts, row);
  // `currentTuition` is always captured (separate from billingPlan).
  // Annual Pricing Increases ("Update your pricing") reads this so it
  // has a starting rate for every family, even when the family isn't
  // on Wonderschool billing yet.
  const currentTuition = computeCurrentTuition(opts, row);

  const studentData: any = {
    firstName: child.firstName,
    lastName: child.lastName,
    displayName: formatFullName({ firstName: child.firstName, lastName: child.lastName }, true),
    birthday: row.childBirthdate ? new Date(row.childBirthdate) : null,
    allergies: row.allergies?.trim() || '',
    gender: row.gender || '',
    defaultOrganization: organizationId,
    defaultLocation: locationId ?? '',
    defaultRoom: roomId,
    organizations: [organizationId],
    locations: locationId ? [locationId] : [],
    rooms: roomId ? [roomId] : [],
    family,
    familyMembers,
    source: EnrollmentSourceEnum.PROVIDER,
    // Enrolled once the start date has arrived, else pending (see `enrolled`
    // above). The legacy boolean and enrollment.status must agree (see
    // setEnrollmentStatus).
    enrollmentStatus: enrolled,
    enrollmentDate: row.startDate ? new Date(row.startDate).valueOf() : null,
    currentTuition,
    enrollment: {
      // Store dates as Date objects (Firestore Timestamps), the shape the rest of
      // CCMS uses. A raw YYYY-MM-DD string renders as "Invalid date" (dateFormatter
      // parses with MM/DD/YYYY) and breaks the enrollment.startDate range queries.
      startDate: row.startDate ? new Date(row.startDate) : null,
      endDate: row.endDate ? new Date(row.endDate) : null,
      schedule: row.schedule || '',
      status: enrolled ? EnrollmentStatusEnum.ENROLLED : EnrollmentStatusEnum.PENDING,
      source: EnrollmentSourceEnum.PROVIDER,
      automaticallyEnroll: false,
      billingPlan,
      currentTuition,
    },
  };

  const studentDocRef = await saveStudent(organizationId, studentData, { merge: false }, batch);

  // Link the child onto the parent Contact's studentIds (arrayUnion, so a
  // reused Contact keeps every sibling instead of the newest one clobbering
  // the last). Mirrors the shape `createContactForStudent` writes for the
  // single-student Add Student flow.
  //
  // In the SAME batch as the contact and student writes: issued afterwards it
  // was a third, non-atomic write, so a failure there left a committed contact
  // and student with no link between them — a child present on the roster but
  // absent from its own parent's `studentIds`.
  await saveContact(
    organizationId,
    { id: contactDocRef.id, studentIds: firestore.FieldValue.arrayUnion(studentDocRef.id) },
    { merge: true },
    batch
  );

  // Contact + student + link commit together — all of it lands or none does.
  await batch.commit();

  // Cache the contact only AFTER the commit. Populated before it, a failed
  // commit left the cache holding an id for a document that was never written;
  // the next row with the same parent email then took the `existingContact`
  // path, and the merge write above CREATES a missing doc — materializing a
  // family contact with nothing but `studentIds`, no email, name, or type, and
  // invisible to the very email lookup that would have deduped it.
  contactCache.set(row.parentEmail.trim().toLowerCase(), contactData);

  return { contactDocRef, studentDocRef, studentData, contactWasExisting: !!existingContact };
}

function computeBillingPlan(opts: ConfirmOptions, row: RosterRow) {
  // Bulk billing path — one plan applied to every queued family
  if (opts.addToBilling && opts.bulkBilling) {
    const amt = Number(opts.bulkBilling.amount.replace(/[^0-9.]/g, ''));
    if (!amt || amt <= 0) return null;
    return {
      frequency: opts.bulkBilling.frequency,
      amount: amt,
      startsOn: opts.bulkBilling.startsOn || row.startDate || null,
      source: 'add_families_wizard',
    };
  }

  // Per-row billing path — only if the row has an amount typed in
  if (!opts.addToBilling && row.billingAmount.trim()) {
    const amt = Number(row.billingAmount.replace(/[^0-9.]/g, ''));
    if (!amt || amt <= 0) return null;
    return {
      frequency: row.billingFrequency,
      amount: amt,
      subsidy: row.subsidy ? Number(row.subsidy.replace(/[^0-9.]/g, '')) : 0,
      startsOn: row.startDate || null,
      note: row.billingNotes?.trim() || '',
      source: 'add_families_wizard',
    };
  }

  return null;
}

/**
 * The "what is this family paying today" value the Annual Pricing
 * Increases tool reads. Captured on EVERY family, regardless of whether
 * they're being put on Wonderschool billing in this wizard run.
 *
 * Priority:
 *   1. Per-row `billingAmount` if typed
 *   2. Bulk-billing amount if `addToBilling` is on
 *   3. null (APR will skip this family)
 *
 * Note this is intentionally separate from `billingPlan` — a row might
 * have a current tuition on file but not be on Wonderschool invoicing
 * yet, and APR still needs to be able to propose a new rate for them.
 */
function computeCurrentTuition(opts: ConfirmOptions, row: RosterRow) {
  // Per-row wins when typed in (always available)
  if (row.billingAmount.trim()) {
    const amt = Number(row.billingAmount.replace(/[^0-9.]/g, ''));
    if (amt > 0) {
      return {
        amount: amt,
        frequency: row.billingFrequency,
        capturedAt: new Date().toISOString(),
        source: 'add_families_wizard',
      };
    }
  }

  // Otherwise fall back to the bulk amount if the user set up Wonderschool billing
  if (opts.addToBilling && opts.bulkBilling) {
    const amt = Number(opts.bulkBilling.amount.replace(/[^0-9.]/g, ''));
    if (amt > 0) {
      return {
        amount: amt,
        frequency: opts.bulkBilling.frequency,
        capturedAt: new Date().toISOString(),
        source: 'add_families_wizard_bulk',
      };
    }
  }

  return null;
}
